import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { CoreService } from '../../../../services/core.service';
import { SubmitGridComponent } from '../../components/submit-grid/submit-grid.component';
import { SectionGridComponent } from '../../components/section-grid/section-grid.component';
import { StepperGridComponent } from '../../components/stepper-grid/stepper-grid.component';
import { FooterGridComponent } from '../../components/footer-grid/footer-grid.component';

@Component({
  selector: 'app-create-feed',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, SectionGridComponent, SubmitGridComponent, StepperGridComponent, FooterGridComponent],
  templateUrl: './create-feed.component.html',
  styleUrl: './create-feed.component.scss'
})
export class CreateFeedComponent implements OnInit {
  @Input() embeddedMode = false;
  @Input() companyIdInput: string | null = null;
  rssFeedForm: FormGroup;
  companyId: string | null = null;
  contentSiteId: string | null = null;
  contentSites: any[] = [];
  hasDefaultSite = false;
  onlyDefaultSite = false;
  showAiGeneratedToggle = true;
  @Input() hasBorder: boolean = true;

  @Output() onSubmitClbk = new EventEmitter<any>();

  constructor(
    private fb: FormBuilder,
    private coreService: CoreService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.rssFeedForm = this.fb.group({
      feed: ['', Validators.required],
      url: [''],
      ms_contentsite_id: [''],
      switchboard_template_name: [''],
      ai_generated: [false],
      status: ['']
    });
  }

  ngOnInit() {
    // Get company ID from route parameter
    this.route.params.subscribe(params => {
      this.companyId = this.companyIdInput || params['company'];
      this.contentSiteId = params['site'];
      console.log('Company ID from route:', this.companyId);
      console.log('Site ID from route:', this.contentSiteId);

      if (this.companyId) {
        // Load content sites for this company
        this.loadContentSites();
      }
    });

    // Check if editing existing feed
    // if (this.formData?.ID) {
    //   this.loadFeedData(this.formData.ID);
    // }
  }

  private loadContentSites(): void {
    this.coreService.getData(`/manageAccount/content-site?companyID=${this.companyId}`).subscribe({
      next: (data) => {
        this.contentSites = data || [];

        // Check if there's a default site
        this.hasDefaultSite = this.contentSites.some(site =>
          site.Domain === 'default' || site.siteUrl === 'default'
        );

        // Check if only default sites exist
        this.onlyDefaultSite = this.contentSites.every(site =>
          site.Domain === 'default' || site.siteUrl === 'default'
        );

        // If contentSiteId is provided from route, pre-fill it
        if (this.contentSiteId) {
          const contentSiteExists = this.contentSites.some(site =>
            site.ID == this.contentSiteId || site.id == this.contentSiteId
          );

          if (contentSiteExists) {
            this.rssFeedForm.patchValue({
              ms_contentsite_id: this.contentSiteId
            });

            // Keep it disabled if it's from route
            this.rssFeedForm.get('ms_contentsite_id')?.disable();
          } else {
            console.warn(`Content site with ID ${this.contentSiteId} not found`);
          }
        } else if (this.hasDefaultSite && !this.rssFeedForm.get('ms_contentsite_id')?.value) {
          // Auto-select default site if no site selected
          const defaultSite = this.contentSites.find(site =>
            site.Domain === 'default' || site.siteUrl === 'default'
          );
          if (defaultSite) {
            this.rssFeedForm.patchValue({
              ms_contentsite_id: defaultSite.ID
            });
          }
        }
      },
      error: (error) => {
        console.error('Error loading content sites:', error);
      }
    });
  }

  private loadFeedData(feedId: string): void {
    this.coreService.getData(`manageAccount/feed/${feedId}`).subscribe({
      next: (data) => {
        this.rssFeedForm.patchValue(data);
      },
      error: (error) => {
        console.error('Error loading feed data:', error);
      }
    });
  }

  // Helper method to get form controls
  getControl(controlName: string) {
    return this.rssFeedForm.get(controlName);
  }

  // Toggle change handlers
  onAiGeneratedChange(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    console.log('AI Generated:', isChecked);
    // Add any additional logic here
  }

  onStatusChange(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    console.log('Status:', isChecked);
    // Add any additional logic here
  }

  onSubmit(): void {
    if (this.rssFeedForm.valid) {
      // Get form values
      const formData = this.rssFeedForm.getRawValue();

      // Add companyID from route params
      const dataToSubmit = {
        ...formData,
        companyID: this.companyId,

      };

      // Determine if this is create or update
      // const endpoint = this.formData?.ID
      //   ? `manageAccount/feed/update/${this.formData.ID}`
      //   : 'manageAccount/feed/store';

      this.coreService.postData('manageAccount/category/store', dataToSubmit).subscribe({
        next: (data) => {
          if (this.embeddedMode) {
            this.onSubmitClbk.emit(data);
          } else {
            console.log('Feed operation successful:', data);

            const companyId = data.companyID || data.data?.companyID;
            const siteId = data.ms_contentsite_id || data.data?.ms_contentsite_id;

            if (companyId) {
              this.router.navigate(['/manage-account/create-video-template', companyId, siteId]);
            } else {
              console.error('No company ID found in response:', data);
            }

            // Optional: Reset form
            // this.resetForm();
          }
        },
        error: (error) => {
          console.error('Error creating/updating feed:', error);

          // Handle specific errors
          if (error.status === 400) {
            alert('Invalid feed data. Please check your inputs.');
          } else if (error.status === 409) {
            alert('Feed already exists for this site.');
          }
        }
      });
    } else {
      this.markFormGroupTouched(this.rssFeedForm);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  resetForm(): void {
    this.rssFeedForm.reset({
      feed: '',
      url: '',
      ms_contentsite_id: this.contentSiteId ||
        (this.hasDefaultSite ?
          this.contentSites.find(s => s.Domain === 'default' || s.siteUrl === 'default')?.ID || '' : ''),
      switchboard_template_name: '',
      ai_generated: false,
      status: ''
    });

    this.rssFeedForm.markAsPristine();
    this.rssFeedForm.markAsUntouched();

    // Re-disable if it was disabled
    if (this.contentSiteId) {
      this.rssFeedForm.get('ms_contentsite_id')?.disable();
    }
  }
}
